import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { Button } from '@/components/ui'
import { hero } from '@/content/site'
import { gsap } from '@/lib/gsap'
import { cx } from '@/lib/cx'
import styles from './Hero.module.css'
import type { HeroState } from './state'
import type { MicStatus } from './voice'

const HOLD_KEYS = new Set([' ', 'Enter'])

/** Props for {@link HoldToTalk}. */
export interface HoldToTalkProps {
  state: HeroState
}

/**
 * Opt-in "Hold to talk": press and hold (pointer, or Space/Enter) to let the orb listen.
 * The mic is analysed locally, frame by frame, and never recorded; every track stops on
 * release, blur or unmount. If the mic is denied or missing, the denied copy shows and the
 * orb keeps talking on its own.
 */
export function HoldToTalk({ state }: HoldToTalkProps) {
  const [status, setStatus] = useState<MicStatus>('idle')
  const buttonRef = useRef<HTMLButtonElement>(null)
  const mic = state.mic

  useEffect(() => {
    mic.onStatus = setStatus
    return () => mic.dispose()
  }, [mic])

  // While listening, the button's ring follows the voice level.
  useEffect(() => {
    const button = buttonRef.current
    if (status !== 'listening' || !button) return
    const tick = () => button.style.setProperty('--vk-level', mic.levels.rms.toFixed(3))
    gsap.ticker.add(tick)
    return () => {
      gsap.ticker.remove(tick)
      button.style.removeProperty('--vk-level')
    }
  }, [status, mic])

  const start = () => {
    void mic.start()
    state.invalidate?.()
  }
  const stop = () => mic.stop()

  const onPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    start()
  }
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!HOLD_KEYS.has(event.key)) return
    event.preventDefault()
    if (!event.repeat) start()
  }
  const onKeyUp = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!HOLD_KEYS.has(event.key)) return
    event.preventDefault()
    stop()
  }

  const listening = status === 'listening' || status === 'requesting'
  const note =
    status === 'listening'
      ? hero.holdToTalk.active
      : status === 'denied'
        ? hero.holdToTalk.denied
        : ''

  return (
    <span className={styles.hold}>
      <Button
        ref={buttonRef}
        variant="ghost"
        icon="mic"
        className={cx(styles.holdButton, listening && styles.holdActive)}
        aria-pressed={listening}
        aria-describedby="hero-mic-note"
        onPointerDown={onPointerDown}
        onPointerUp={stop}
        onPointerCancel={stop}
        onLostPointerCapture={stop}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onBlur={stop}
        onContextMenu={(event) => event.preventDefault()}
      >
        {hero.holdToTalk.idle}
      </Button>
      <span id="hero-mic-note" className={styles.holdNote} role="status" aria-live="polite">
        {note}
      </span>
    </span>
  )
}
